import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';
import { Shield, FileText, CheckCircle, AlertCircle, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ConsentModal = ({ open, onAccept, onDismiss }) => {
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);

  const handleAccept = async () => {
    if (!agreed) {
      toast.error('Please check the box to confirm you have read and agree to the terms');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found. Please log in again.');
      }

      const response = await axios.post(
        `${API}/users/accept-terms`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      console.log('Terms accepted successfully:', response.data);
      toast.success('Thank you for accepting our terms and privacy policy');
      onAccept();
    } catch (error) {
      console.error('Error accepting terms:', error);
      console.error('Error details:', error.response?.data);
      
      // Better error messages
      if (error.response?.status === 401) {
        // Don't show error for 401 - just proceed, they can accept later
        console.warn('Session expired while accepting terms, user can try again');
        toast.info('Please try accepting again after refreshing the page');
        onAccept(); // Close modal anyway to reduce disruption
      } else if (error.response?.status === 403) {
        toast.error('Access denied. Please contact support.');
      } else if (error.message && !error.message.includes('token')) {
        toast.error(error.message);
      } else {
        toast.error('Could not save consent. You can continue and try again later from Settings.');
        // Allow them to proceed anyway to reduce disruption
        setTimeout(() => onAccept(), 2000);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    if (onDismiss) {
      onDismiss();
    }
  };

  // Privacy Policy Content Component
  const PrivacyPolicyContent = () => (
    <div className="space-y-6 max-h-[70vh] overflow-y-auto p-6 bg-slate-50 rounded-lg">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bold text-slate-900">Privacy Policy</h2>
        <button onClick={() => setShowPrivacy(false)} className="text-slate-500 hover:text-slate-700">
          <X size={24} />
        </button>
      </div>
      
      <p className="text-sm text-slate-600">Last updated: December 13, 2024</p>
      
      <section className="space-y-3">
        <h3 className="text-lg font-semibold text-slate-900">1. Information We Collect</h3>
        <p className="text-slate-700"><strong>Personal Information:</strong></p>
        <ul className="list-disc list-inside ml-4 space-y-1 text-slate-700">
          <li>Name, birthdate, sex, and contact details</li>
          <li>Login credentials (encrypted)</li>
          <li>Profile photo (optional)</li>
        </ul>
        <p className="text-slate-700 mt-3"><strong>Health Information:</strong></p>
        <ul className="list-disc list-inside ml-4 space-y-1 text-slate-700">
          <li>Medical history and health goals</li>
          <li>Clinical data (medications, allergies)</li>
          <li>Visit logs and provider notes</li>
        </ul>
      </section>

      <section className="space-y-3">
        <h3 className="text-lg font-semibold text-slate-900">2. How We Use Your Information</h3>
        <ul className="list-disc list-inside ml-4 space-y-1 text-slate-700">
          <li>Provide healthcare services and manage your care</li>
          <li>Communicate with you about appointments and updates</li>
          <li>Improve our platform and user experience</li>
          <li>Ensure security and protect against unauthorized access</li>
          <li>Comply with legal obligations</li>
        </ul>
      </section>

      <section className="space-y-3">
        <h3 className="text-lg font-semibold text-slate-900">3. How We Protect Your Information</h3>
        <ul className="list-disc list-inside ml-4 space-y-1 text-slate-700">
          <li>HTTPS/SSL encryption for all data transmission</li>
          <li>Password hashing and secure authentication</li>
          <li>Role-based access controls</li>
          <li>Regular security audits and updates</li>
          <li>Auto-logout after 30 minutes of inactivity</li>
        </ul>
      </section>

      <section className="space-y-3">
        <h3 className="text-lg font-semibold text-slate-900">4. Data Sharing</h3>
        <p className="text-slate-700">
          We <strong>never sell</strong> your data. We only share information with:
        </p>
        <ul className="list-disc list-inside ml-4 space-y-1 text-slate-700">
          <li>Your assigned healthcare providers</li>
          <li>Service providers bound by confidentiality agreements</li>
          <li>Legal authorities when required by law</li>
        </ul>
      </section>

      <section className="space-y-3">
        <h3 className="text-lg font-semibold text-slate-900">5. Your Rights (Philippine Data Privacy Act)</h3>
        <ul className="list-disc list-inside ml-4 space-y-1 text-slate-700">
          <li>Right to access your personal data</li>
          <li>Right to correct inaccurate information</li>
          <li>Right to delete your data (subject to legal requirements)</li>
          <li>Right to object to data processing</li>
          <li>Right to data portability</li>
        </ul>
        <p className="text-slate-700 mt-2">
          For more information, visit: <a href="https://privacy.gov.ph" target="_blank" rel="noopener noreferrer" className="text-cyan-600 underline">privacy.gov.ph</a>
        </p>
      </section>

      <section className="space-y-3">
        <h3 className="text-lg font-semibold text-slate-900">6. Contact Us</h3>
        <p className="text-slate-700">
          If you have questions about this policy or your data, contact us at: support@wearepilgrims.site
        </p>
      </section>
    </div>
  );

  // Terms of Service Content Component
  const TermsOfServiceContent = () => (
    <div className="space-y-6 max-h-[70vh] overflow-y-auto p-6 bg-slate-50 rounded-lg">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bold text-slate-900">Terms of Service</h2>
        <button onClick={() => setShowTerms(false)} className="text-slate-500 hover:text-slate-700">
          <X size={24} />
        </button>
      </div>
      
      <p className="text-sm text-slate-600">Last updated: December 13, 2024</p>

      <section className="space-y-3">
        <h3 className="text-lg font-semibold text-slate-900">1. Acceptance of Terms</h3>
        <p className="text-slate-700">
          By accessing and using We Are Pilgrims, you accept and agree to be bound by these Terms of Service. If you do not agree, please do not use our platform.
        </p>
      </section>

      <section className="space-y-3">
        <h3 className="text-lg font-semibold text-slate-900">2. Eligibility</h3>
        <ul className="list-disc list-inside ml-4 space-y-1 text-slate-700">
          <li>You must be at least 18 years old to use this service</li>
          <li>Minors may use with parental consent</li>
          <li>You must provide accurate information when creating your account</li>
        </ul>
      </section>

      <section className="space-y-3">
        <h3 className="text-lg font-semibold text-slate-900">3. User Accounts</h3>
        <ul className="list-disc list-inside ml-4 space-y-1 text-slate-700">
          <li>You are responsible for maintaining the confidentiality of your password</li>
          <li>You must notify us immediately of any unauthorized access</li>
          <li>We reserve the right to suspend accounts that violate these terms</li>
        </ul>
      </section>

      <section className="space-y-3">
        <h3 className="text-lg font-semibold text-slate-900">4. Use of Platform</h3>
        <p className="text-slate-700"><strong>You agree to:</strong></p>
        <ul className="list-disc list-inside ml-4 space-y-1 text-slate-700">
          <li>Provide accurate and complete health information</li>
          <li>Use the platform only for lawful purposes</li>
          <li>Respect the privacy of other users</li>
          <li>Not attempt to disrupt or compromise platform security</li>
        </ul>
      </section>

      <section className="space-y-3">
        <h3 className="text-lg font-semibold text-slate-900">5. Healthcare Disclaimer</h3>
        <p className="text-slate-700">
          <strong>Important:</strong> We Are Pilgrims is a platform for healthcare management. It does not replace professional medical advice. Always consult with qualified healthcare providers for medical decisions.
        </p>
      </section>

      <section className="space-y-3">
        <h3 className="text-lg font-semibold text-slate-900">6. Limitation of Liability</h3>
        <p className="text-slate-700">
          We strive to provide a secure and reliable platform, but we are not liable for:
        </p>
        <ul className="list-disc list-inside ml-4 space-y-1 text-slate-700">
          <li>Interruptions in service or technical issues</li>
          <li>Loss of data due to circumstances beyond our control</li>
          <li>Medical outcomes or decisions made by healthcare providers</li>
        </ul>
      </section>

      <section className="space-y-3">
        <h3 className="text-lg font-semibold text-slate-900">7. Termination</h3>
        <p className="text-slate-700">
          We reserve the right to terminate or suspend access to our service for violations of these terms or for any other reason at our discretion.
        </p>
      </section>

      <section className="space-y-3">
        <h3 className="text-lg font-semibold text-slate-900">8. Changes to Terms</h3>
        <p className="text-slate-700">
          We may update these terms from time to time. Continued use of the platform after changes constitutes acceptance of the new terms.
        </p>
      </section>

      <section className="space-y-3">
        <h3 className="text-lg font-semibold text-slate-900">9. Governing Law</h3>
        <p className="text-slate-700">
          These terms are governed by the laws of the Republic of the Philippines.
        </p>
      </section>

      <section className="space-y-3">
        <h3 className="text-lg font-semibold text-slate-900">10. Contact Information</h3>
        <p className="text-slate-700">
          For questions about these terms, contact us at: support@wearepilgrims.site
        </p>
      </section>
    </div>
  );

  // Show policy pop-up if user clicks to view
  if (showPrivacy) {
    return (
      <Dialog open={true} onOpenChange={() => setShowPrivacy(false)}>
        <DialogContent className="max-w-3xl">
          <PrivacyPolicyContent />
          <Button onClick={() => setShowPrivacy(false)} className="mt-4 w-full">
            Close
          </Button>
        </DialogContent>
      </Dialog>
    );
  }

  if (showTerms) {
    return (
      <Dialog open={true} onOpenChange={() => setShowTerms(false)}>
        <DialogContent className="max-w-3xl">
          <TermsOfServiceContent />
          <Button onClick={() => setShowTerms(false)} className="mt-4 w-full">
            Close
          </Button>
        </DialogContent>
      </Dialog>
    );
  }

  // Main consent modal
  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) handleDismiss(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 text-2xl">
            <div className="p-2 bg-cyan-100 rounded-full">
              <Shield size={28} className="text-cyan-700" />
            </div>
            Welcome to We Are Pilgrims
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Welcome Message */}
          <Alert className="bg-cyan-50 border-cyan-200">
            <CheckCircle className="h-4 w-4 text-cyan-600" />
            <AlertDescription className="text-cyan-900">
              <strong>Your privacy and security are our top priorities.</strong> Before you begin, 
              please take a moment to review our policies.
            </AlertDescription>
          </Alert>

          {/* Security Features */}
          <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
            <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
              <Shield size={18} className="text-cyan-600" />
              Your Data is Protected
            </h3>
            <ul className="space-y-2 text-sm text-slate-700">
              <li className="flex items-start gap-2">
                <CheckCircle size={16} className="text-green-600 flex-shrink-0 mt-0.5" />
                <span><strong>Encrypted connections:</strong> All data is transmitted securely using HTTPS/SSL</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle size={16} className="text-green-600 flex-shrink-0 mt-0.5" />
                <span><strong>Secure passwords:</strong> Your password is encrypted and never stored in plain text</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle size={16} className="text-green-600 flex-shrink-0 mt-0.5" />
                <span><strong>Role-based access:</strong> Only your assigned healthcare providers can view your records</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle size={16} className="text-green-600 flex-shrink-0 mt-0.5" />
                <span><strong>Auto-logout:</strong> Sessions expire after 30 minutes of inactivity</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle size={16} className="text-green-600 flex-shrink-0 mt-0.5" />
                <span><strong>No data sharing:</strong> We never sell your information to third parties</span>
              </li>
            </ul>
          </div>

          {/* Important Documents */}
          <div className="space-y-3">
            <h3 className="font-semibold text-slate-900 flex items-center gap-2">
              <FileText size={18} className="text-blue-600" />
              Please Review Our Policies
            </h3>
            
            <div className="grid gap-3">
              <button
                onClick={() => setShowPrivacy(true)}
                className="flex items-center justify-between p-4 border-2 border-slate-200 rounded-lg hover:border-cyan-300 hover:bg-cyan-50 transition-colors text-left"
              >
                <div className="flex items-center gap-3">
                  <Shield size={24} className="text-cyan-600" />
                  <div>
                    <p className="font-semibold text-slate-900">Privacy Policy</p>
                    <p className="text-xs text-slate-600">How we collect, use, and protect your data</p>
                  </div>
                </div>
                <span className="text-cyan-700 text-sm font-medium">View →</span>
              </button>

              <button
                onClick={() => setShowTerms(true)}
                className="flex items-center justify-between p-4 border-2 border-slate-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors text-left"
              >
                <div className="flex items-center gap-3">
                  <FileText size={24} className="text-blue-600" />
                  <div>
                    <p className="font-semibold text-slate-900">Terms of Service</p>
                    <p className="text-xs text-slate-600">Your rights and responsibilities</p>
                  </div>
                </div>
                <span className="text-blue-700 text-sm font-medium">View →</span>
              </button>
            </div>
          </div>

          {/* Philippine Data Privacy Act Notice */}
          <Alert className="bg-blue-50 border-blue-200">
            <AlertCircle className="h-4 w-4 text-blue-600" />
            <AlertDescription className="text-blue-900 text-sm">
              <strong>Philippine Data Privacy Act:</strong> You have the right to access, correct, and delete your 
              personal data. For more information about your rights, visit{' '}
              <a 
                href="https://privacy.gov.ph" 
                target="_blank" 
                rel="noopener noreferrer"
                className="underline font-medium"
              >
                privacy.gov.ph
              </a>
            </AlertDescription>
          </Alert>

          {/* Consent Checkbox */}
          <div className="bg-white border-2 border-slate-300 rounded-lg p-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                className="mt-1 w-5 h-5 text-cyan-600 border-slate-300 rounded focus:ring-cyan-500 cursor-pointer"
              />
              <span className="text-sm text-slate-700 leading-relaxed">
                I have read and agree to the Privacy Policy and Terms of Service. I understand how my personal health information will be collected, used, and protected.
              </span>
            </label>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3 pt-4 border-t">
            <Button
              onClick={handleAccept}
              disabled={!agreed || loading}
              className="w-full bg-cyan-600 hover:bg-cyan-700 text-white font-semibold py-3 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Processing...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <CheckCircle size={18} />
                  I Agree - Continue to Dashboard
                </span>
              )}
            </Button>

            <Button
              onClick={handleDismiss}
              disabled={loading}
              variant="outline"
              className="w-full border-2 border-slate-300 text-slate-700 hover:bg-slate-50 font-medium py-3 rounded-lg"
            >
              I'll Review the Policies First
            </Button>
          </div>

          <p className="text-xs text-slate-500 text-center">
            By continuing, you confirm that you are at least 18 years old or have parental consent
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ConsentModal;
